import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../../ServiceUser/component/ServiceUserTable.module.css';
import dayjs from 'dayjs';
import { TableStateType } from '../../../utils/contextapi/TableProvider';
import { InnocodeButton, InnocodeChip, InnocodeDatatable, InnocodeInput, openInnocodeDatePickerRange } from '@intsoft/innocode-core';

const MEMBERSHIP_TYPE_LABEL = {
    PROFILE: '프로필',
    TENANT: '테넌트',
    EMAIL: '이메일',
};

const ServiceUserApprovalTable = ({
    datas,
    context,
    onApprove,
    onReject,
    onPrevPage,
    onNextPage,
    disablePrev,
    disableNext,
    pageNumber,
    totalCount,
    listSize,
    searchValue,
    setSearchValue,
    onSearch,
    isMobileWindow,
}) => {
    const [isInitialMount, setIsInitialMount] = useState(true);

    useEffect(() => {
        if (isInitialMount) {
            setIsInitialMount(false);
            return;
        }

        if (context.state.dateRange[0] || context.state.dateRange[1]) {
            onSearch('approvalKeyword', searchValue);
        }
    }, [context.state.dateRange]);

    const handleDateRangeClick = useCallback(async () => {
        const pickedDateRange = await openInnocodeDatePickerRange({
            initialDate: [context.state.dateRange[0], context.state.dateRange[1]],
            minDate: new Date(2020, 0, 1),
            maxDate: new Date(),
            mode: 'range',
        });

        const newStartDate = pickedDateRange != null && pickedDateRange.startDate ? new Date(pickedDateRange.startDate) : null;
        const newEndDate = pickedDateRange != null && pickedDateRange.endDate ? new Date(pickedDateRange.endDate) : null;

        if (newStartDate && newEndDate) {
            context.actions.contextDispatch({
                type: TableStateType.DATE_RANGE,
                data: [newStartDate, newEndDate],
            });
        }
    }, [context.state.dateRange, context.actions]);

    const formatDateRange = useCallback(() => {
        const start = context.state.dateRange[0];
        const end = context.state.dateRange[1];

        if (!start && !end) {
            return '날짜 범위 선택';
        }

        const startStr = start ? dayjs(start).format('YYYY-MM-DD') : '';
        const endStr = end ? dayjs(end).format('YYYY-MM-DD') : '';

        if (startStr && endStr) {
            return `${startStr} ~ ${endStr}`;
        } else if (startStr) {
            return startStr;
        } else if (endStr) {
            return endStr;
        }

        return '날짜 범위 선택';
    }, [context.state.dateRange]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage > pageNumber) {
            onNextPage();
        } else {
            onPrevPage();
        }
    }, [pageNumber, onNextPage, onPrevPage]);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                id: 'userId',
                header: 'ID',
                accessorKey: 'userId',
                enableSorting: true,
                cell: ({ row }) => <span>{row.userId || row.profileId || '-'}</span>,
            },
            {
                id: 'profile',
                header: '프로필',
                accessorKey: 'name',
                enableSorting: false,
                cell: ({ row }) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                            src={row.imgUrl || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                            alt="프로필 이미지"
                            style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                        />
                        <span>{row.name || '-'}</span>
                    </div>
                ),
            },
            {
                id: 'membershipType',
                header: '가입 유형',
                accessorKey: 'membershipType',
                enableSorting: false,
                cell: ({ row }) => <span>{MEMBERSHIP_TYPE_LABEL[row.membershipType] || row.membershipType || '-'}</span>,
            },
            {
                id: 'authorityIds',
                header: '요청 권한',
                accessorKey: 'authorityIds',
                enableSorting: false,
                cell: ({ row }) => <span>{Array.isArray(row.auth) ? row.auth.join(', ') : '-'}</span>,
            },
        ];

        if (!isMobileWindow) {
            baseColumns.push({
                id: 'createdDate',
                header: '요청일',
                accessorKey: 'createdDate',
                enableSorting: true,
                cell: ({ row }) => <span>{row.createdDate ? dayjs(Number(row.createdDate)).format('YYYY-MM-DD') : '-'}</span>,
            });
        }

        baseColumns.push({
            id: 'action',
            header: '처리',
            accessorKey: 'action',
            enableSorting: false,
            cell: ({ row }) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <InnocodeButton
                        size="sm"
                        onClick={(event) => {
                            event.stopPropagation();
                            onApprove(row.serviceUserId);
                        }}
                    >
                        승인
                    </InnocodeButton>
                    <InnocodeButton
                        size="sm"
                        theme="line"
                        designType="primary"
                        onClick={(event) => {
                            event.stopPropagation();
                            onReject(row.serviceUserId);
                        }}
                    >
                        거부
                    </InnocodeButton>
                </div>
            ),
        });

        return baseColumns;
    }, [isMobileWindow, onApprove, onReject]);

    return (
        <div className={styles.tableSection}>
            <InnocodeDatatable
                className={styles.dataTable}
                data={datas}
                columns={columns}
                enableCustomHeader={true}
                customHeader={
                    <div className={styles.customHeaderContainer}>
                        <div className={styles.customHeaderSearchContainer} style={{ justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ width: isMobileWindow ? '100%' : '360px', minWidth: isMobileWindow ? '100%' : '280px', flex: '0 0 auto' }}>
                                <InnocodeInput
                                    className={styles.customHeaderSearchInput}
                                    placeholder="프로필명 또는 이메일 검색"
                                    showTrailingContent={true}
                                    value={searchValue}
                                    onChange={(event) => setSearchValue(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            onSearch('approvalKeyword', searchValue.trim());
                                        }
                                    }}
                                    trailingContent={
                                        <InnocodeButton
                                            enablePadding={false}
                                            size="xs"
                                            theme="opacity"
                                            designType="alternative"
                                            iconOnly={true}
                                            icon={
                                                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                    <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
                                                </svg>
                                            }
                                            onClick={() => onSearch('approvalKeyword', searchValue.trim())}
                                        />
                                    }
                                />
                            </div>
                            <div className={styles.datePickerWrapper} style={{ width: 'auto', flex: '0 0 auto' }}>
                                <InnocodeChip
                                    size="lg"
                                    theme="line"
                                    type="primary"
                                    onClick={handleDateRangeClick}
                                    icon={
                                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path fillRule="evenodd" d="M5 5a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1h1a1 1 0 0 0 1-1 1 1 0 1 1 2 0 1 1 0 0 0 1 1 2 2 0 0 1 2 2v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2ZM3 19v-7a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm6.01-6a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm-10 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm6 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0Zm2 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z" clipRule="evenodd" />
                                        </svg>
                                    }
                                >
                                    {formatDateRange()}
                                </InnocodeChip>
                            </div>
                        </div>
                    </div>
                }
                enableTableHeader={true}
                enableSorting={true}
                enableCheckbox={false}
                enablePagination={true}
                manualPagination={true}
                pageSize={listSize}
                totalCount={totalCount}
                currentPage={pageNumber}
                onPageChange={handlePageChange}
                emptyMessage="승인 대기 중인 서비스 사용자가 없습니다."
                rowClassName={styles.tableRow}
            />
        </div>
    );
};

export default ServiceUserApprovalTable;
